import React from "react";
import { WorkflowMode } from "../../common/enums";
import { BaseFullScreenTakeover } from "../../Components/BaseFullScreenTakeover";
import { PaymentPrefConfirmation } from "../../Components/PaymentPrefConfirmation";
import { PositionSingleDisplay } from "../../Components/PositionSingleDisplay";
import { SelectPositionsToUpdate } from "../../Components/SelectPositionsToUpdate";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { PaymentPrefCreationForPositionWorkflow } from "../PaymentPrefCreationForPositionWorkflow";
import { PaymentPrefEditForAccountAndPositionWorkflow } from "../PaymentPrefEditForAccountAndPositionWorkflow";
import { PaymentPrefForPositionWorkflowProps } from "./types";

/**
 * PaymentPrefForPositionWorkflow component.
 *
 * This component handles the workflow for creating or editing a payment preference for a specific position.
 * It determines the workflow mode (create or edit) and renders the appropriate workflow component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onExit - Function to call when the workflow is exited.
 * @param {Function} props.onSuccess - Function to call when the workflow is successfully completed.
 * @param {WorkflowMode} props.workflowMode - Enum indicating the workflow mode (create or edit).
 * @param {Object} props.workflowParams - Object containing additional parameters for the workflow type discriminated by the workflow mode.
 * @param {ExistingPaymentPref} [props.workflowParams.existingPaymentPref] - The existing payment preference to be edited (required if workflowMode is "edit").
 * @param {Position} [props.workflowParams.position] - The related position to target.
 *
 * @example For creating a payment preference
 * <PaymentPrefForPositionWorkflow
 *   onExit={handleExit}
 *   onSuccess={handleSuccess}
 *   workflowMode={WorkflowMode.CREATE}
 *   workflowParams={{ position: { accountId: '123', investmentEntityId: '456' } }}
 *   arena={{ id: 'globalId', features: { hasPositionPaymentPrefStaffApprovals: true } }}
 * />
 *
 * @example For editing a payment preference
 * <PaymentPrefForPositionWorkflow
 *   onExit={handleExit}
 *   onSuccess={handleSuccess}
 *   workflowMode={WorkflowMode.EDIT}
 *   workflowParams={{ position: { accountId: '123', investmentEntityId: '456' }, existingPaymentPref: { id: '789', paymentMethod: 'ACH' } }}
 *   arena={{ id: 'globalId', features: { hasPositionPaymentPrefStaffApprovals: true } }}
 * />
 *
 * @todo move this to a _actual_ "closed component" barrel file for dir.
 */

export const PaymentPrefForPositionWorkflow: React.FC<PaymentPrefForPositionWorkflowProps> = ({
  onExit,
  onSuccess,
  workflowMode,
  workflowParams,
  accountName,
  arena,
}) => {
  if (workflowMode === WorkflowMode.Create) {
    return (
      <BaseFullScreenTakeover onExit={onExit} title="Update payment instructions">
        <PaymentPrefCreationForPositionWorkflow
          position={workflowParams.position}
          sourcePaymentPrefId={workflowParams.sourcePaymentPrefId}
          onExit={onExit}
          onSuccess={onSuccess}
          arena={arena}
        />
      </BaseFullScreenTakeover>
    );
  } else if (workflowMode === WorkflowMode.Edit) {
    return (
      <BaseFullScreenTakeover onExit={onExit} title="Update payment instructions">
        <PaymentPrefEditForAccountAndPositionWorkflow
          ListOfPositionsToUpdateComponent={PositionSingleDisplay}
          position={workflowParams.position}
          existingPaymentPref={workflowParams.existingPaymentPref}
          initialAssociatePaymentPref={AssociatePaymentPrefMutationTypeEnum.OnlyThisPosition}
          onExit={onExit}
          onSuccess={onSuccess}
          suffixComponents={[SelectPositionsToUpdate, PaymentPrefConfirmation]}
          accountName={accountName}
          arena={arena}
        />
      </BaseFullScreenTakeover>
    );
  }

  /**
   * If the workflow mode is not supported, throw an error.
   */
  throw new Error("Unsupported workflow mode");
};

export type { PaymentPrefForPositionWorkflowProps };
